import React, { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneInput from "./PhoneInput/PhoneInput.js";
import DateInput from "./DateInput/DateInput.js";

import doctorsData from "../../Data/specialists/specialistsData.json";
import receptionSchedule from "../../Data/specialists/receptionSchedule.json";

const OMS_DOCTORS = [
  "romanovich",
  "kokorina",
  "rabtsevich",
  "farlenkova",
  "malyavina",
];

export default function Record() {
  const [formData, setFormData] = useState({
    fio: "",
    phone: "+7",
    date: "", // ВСЕГДА DD.MM.YYYY
    type: "",
  });

  const [isAgree, setIsAgree] = useState(false);
  const [isOmsAgree, setIsOmsAgree] = useState(false);
  const [isDateValid, setIsDateValid] = useState(false);
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [message, setMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [popupStatus, setPopupStatus] = useState("idle");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const recaptchaRef = useRef(null);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [doctorSchedule, setDoctorSchedule] = useState(null);

  const isOmsAvailable = OMS_DOCTORS.includes(selectedDoctor);

  const isFioValid = formData.fio.trim().length > 0;
  const isPhoneValid = formData.phone.replace(/\D/g, "").length >= 11;

  const getErrorClass = (isValid) =>
    wasSubmitted && !isValid ? "record__input--error" : "";

  const getErrorMessage = (isValid, text) =>
    wasSubmitted && !isValid ? (
      <span className="record__field-error">{text}</span>
    ) : null;

  const API_URL =
    process.env.NODE_ENV === "production"
      ? "https://api.urniidvi.ru"
      : "http://localhost:5000";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ---------- DATE HELPERS ----------
  const toRuDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };

  const toIsoDate = (ru) => {
    if (!ru) return "";
    const [d, m, y] = ru.split(".");
    return `${y}-${m}-${d}`;
  };

  // ---------- DOCTORS ----------
  const availableDoctors = [];
  receptionSchedule.forEach((section) => {
    section.doctors?.forEach((doctor) => {
      const hasWork =
        Array.isArray(doctor.work) &&
        doctor.work.some((d) => typeof d === "string" && d.trim());

      if (hasWork) {
        availableDoctors.push({
          ...doctor,
          key: `${doctor.slug}-${section.section}`, // 👈 FIX DUPLICATE KEY
        });
      }
    });
  });

  const handleDoctorChange = (e) => {
    const slug = e.target.value;

    setSelectedDoctor(slug);

    let found = null;
    let type = "";

    receptionSchedule.forEach((section) => {
      const doc = section.doctors?.find((d) => d.slug === slug);

      if (doc) {
        found = doc;

        // if (section.section === "Дерматология взрослая") type = "paid";
        // if (section.section === "Детский дерматологический приём")
        //   type = "paid";
      }
    });

    setDoctorSchedule(found || null);

    const omsAvailable = OMS_DOCTORS.includes(slug);

    setFormData((prev) => ({
      ...prev,
      date: "",
      type: omsAvailable ? prev.type || "paid" : "paid",
    }));

    setIsOmsAgree(false);
    setIsDateValid(false);
  };

  // ---------- DATES ----------
  const generateAvailableDates = (schedule) => {
    if (!schedule?.work) return [];

    const workDays = schedule.work
      .map((day, i) => (day && day.trim() !== "-" ? i + 1 : null))
      .filter(Boolean);

    const result = [];
    const today = new Date();

    for (let i = 0; i < 15; i++) {
      const d = new Date();
      d.setDate(today.getDate() + i);

      const jsDay = d.getDay() || 7;

      if (workDays.includes(jsDay)) {
        result.push(d);
      }
    }

    return result;
  };

  const formatListDate = (date) =>
    date.toLocaleDateString("ru-RU", {
      weekday: "short",
      day: "2-digit",
      month: "long",
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setWasSubmitted(true);
    setMessage("");

    if (!isFioValid) {
      setMessage("Введите ФИО");
      return;
    }

    if (!isDateValid) {
      setMessage("Введите корректную дату");
      return;
    }

    const phoneDigits = formData.phone.replace(/\D/g, "");
    if (phoneDigits.length < 11) {
      setMessage("Введите корректный телефон");
      return;
    }

    const recaptchaValue = recaptchaRef.current.getValue();
    if (!recaptchaValue) {
      setMessage("Подтвердите капчу");
      return;
    }

    setIsSubmitting(true);
    setShowPopup(true);
    setPopupStatus("sending");

    try {
      const res = await fetch(`${API_URL}/api/send-mail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fio: formData.fio,
          phone: formData.phone,
          date: formData.date,
          doctor:
            doctorsData.find((d) => d.slug === selectedDoctor)?.name ||
            "Не выбран",
          type: formData.type === "oms" ? "ОМС" : "Платный",
          to_email: "urniidvi-online@mail.ru",
        }),
      });

      if (!res.ok) throw new Error("Ошибка");

      setFormData({ fio: "", phone: "+7", date: "", type: "" });
      setPopupStatus("success");
      recaptchaRef.current.reset();
      setCaptchaVerified(false);
    } catch (err) {
      setPopupStatus("error");
      setMessage(err.message);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setPopupStatus("idle");
    setIsSubmitting(false);

    recaptchaRef.current?.reset();
    setCaptchaVerified(false);
  };

  return (
    <section id="record" className="record">
      <form onSubmit={handleSubmit} className="record__input-box">
        {/* {wasSubmitted && message && (
          <p className="record__error-message" role="alert">
            {message}
          </p>
        )} */}
        <div className="record__field">
          <input
            // className="record__input"
            className={`record__input ${getErrorClass(isFioValid)}`}
            name="fio"
            placeholder="ФИО"
            value={formData.fio}
            onChange={handleChange}
          />
          {getErrorMessage(isFioValid, "Введите ФИО")}
        </div>

        <div className="record__field">
          <PhoneInput
            // className="record__input"
            className={`record__input ${getErrorClass(isPhoneValid)}`}
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
          {getErrorMessage(isPhoneValid, "Введите корректный телефон")}
        </div>

        {/* DOCTORS */}
        <select
          className="record__input"
          value={selectedDoctor}
          onChange={handleDoctorChange}
        >
          <option value="">Выбрать врача</option>

          {availableDoctors.map((doc) => (
            <option key={doc.key} value={doc.slug}>
              {doctorsData.find((d) => d.slug === doc.slug)?.name}
            </option>
          ))}
        </select>

        {/* DATE */}
        <div className="record__field">
          {doctorSchedule ? (
            <select
              // className="record__input"
              className={`record__input ${getErrorClass(isDateValid)}`}
              value={toIsoDate(formData.date)}
              onChange={(e) => {
                const ru = toRuDate(e.target.value);

                setFormData((p) => ({ ...p, date: ru }));
                setIsDateValid(true);
              }}
            >
              <option value="">Выберите дату</option>

              {generateAvailableDates(doctorSchedule).map((d) => {
                const iso = d.toISOString().split("T")[0];

                return (
                  <option key={iso} value={iso}>
                    {formatListDate(d)}
                  </option>
                );
              })}
            </select>
          ) : (
            <DateInput
              // className="record__input"
              className={`record__input ${getErrorClass(isDateValid)}`}
              name="date"
              value={formData.date}
              onChange={handleChange}
              onValidityChange={setIsDateValid}
            />
          )}
          {getErrorMessage(isDateValid, "Выберите или введите корректную дату")}
        </div>

        {/* TYPE */}
        <select
          className="record__input"
          name="type"
          value={formData.type}
          onChange={(e) => {
            const type = e.target.value;

            setFormData((prev) => ({
              ...prev,
              type,
            }));

            setIsOmsAgree(false);
          }}
        >
          <option value="">Прием</option>
          <option value="paid">Платный</option>

          {isOmsAvailable && <option value="oms">ОМС</option>}
        </select>

        <div style={{ margin: "10px 0" }}>
          <ReCAPTCHA
            sitekey="6LdGG7srAAAAAGK6wMRUdW1xdkSsS6areWMsJhsW"
            ref={recaptchaRef}
            onChange={(token) => setCaptchaVerified(!!token)}
            onExpired={() => setCaptchaVerified(false)}
          />
        </div>

        <label className="record__agree">
          {" "}
          <input
            type="checkbox"
            checked={isAgree}
            onChange={() => setIsAgree(!isAgree)}
          />{" "}
          Я даю согласие на обработку персональных данных ({" "}
          <a
            href="/about/privacy"
            target="_blank"
            rel="noreferrer"
            className="question-answer-text"
          >
            {" "}
            политика{" "}
          </a>{" "}
          ){" "}
        </label>

        {formData.type === "oms" && (
          <label className="record__agree">
            <input
              type="checkbox"
              checked={isOmsAgree}
              onChange={(e) => setIsOmsAgree(e.target.checked)}
            />{" "}
            Я уведомлен, что без направления формы 057/у прием по ОМС
            невозможен, и согласен с условиями.
          </label>
        )}

        <button
          type="submit"
          className="record__button"
          disabled={
            isSubmitting ||
            showPopup ||
            !isAgree ||
            !captchaVerified ||
            (formData.type === "oms" && !isOmsAgree)
          } // 🔒 блок при отправке и при открытом попапе
          style={{
            background:
              isSubmitting ||
              showPopup ||
              !isAgree ||
              !captchaVerified ||
              (formData.type === "oms" && !isOmsAgree)
                ? "#d9d9d9"
                : "#5585c7",
            color:
              isSubmitting ||
              showPopup ||
              !isAgree ||
              !captchaVerified ||
              (formData.type === "oms" && !isOmsAgree)
                ? "#888"
                : "#fff",
            cursor:
              isSubmitting ||
              showPopup ||
              !isAgree ||
              !captchaVerified ||
              (formData.type === "oms" && !isOmsAgree)
                ? "not-allowed"
                : "pointer",
            opacity:
              isSubmitting ||
              showPopup ||
              !isAgree ||
              !captchaVerified ||
              (formData.type === "oms" && !isOmsAgree)
                ? 0.7
                : 1,
          }}
        >
          {" "}
          {isSubmitting ? "Отправка..." : "Заказать звонок"}{" "}
        </button>
      </form>

      {showPopup && (
        <div className="popup__overlay" onClick={handleClosePopup}>
          <div className="popup__content" onClick={(e) => e.stopPropagation()}>
            {popupStatus === "sending" && (
              <>
                <h3 className="popup__title">Отправка...</h3>
                <p className="popup__text">Пожалуйста, подождите.</p>
              </>
            )}

            {popupStatus === "success" && (
              <>
                <h3 className="popup__title">Заявка отправлена!</h3>
                <p className="popup__text">
                  Администратор скоро свяжется с вами.
                </p>
              </>
            )}

            {popupStatus === "error" && (
              <>
                <h3 className="popup__title">Ошибка</h3>
                <p className="popup__text">Не удалось отправить заявку.</p>
              </>
            )}

            <button className="record__button" onClick={handleClosePopup}>
              Закрыть
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
